// src/components/Properties.jsx

import React, { useState, useEffect, useContext } from 'react';
import { auth } from '../firebaseClient';
import { onAuthStateChanged } from 'firebase/auth';
import './Properties.css';
import { CustomerContext } from '../App';
import { useNavigate } from 'react-router-dom';

function formatFloat(value) {
  const num = parseFloat(value);
  if (isNaN(num)) return '0.00';
  return num.toFixed(2);
}

function formatDate(dateString) {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (isNaN(date)) return dateString;
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
}

function Properties() {
  const [viewMode, setViewMode] = useState('property');
  const [properties, setProperties] = useState([]);
  const [objects, setObjects] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedObject, setSelectedObject] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { selectedCustomerId } = useContext(CustomerContext);
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

  // 1) Admin-Status ermitteln
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, user => {
      const adminEmails = [
        'prosperly.info@gmail.com'
      ];
      setIsAdmin(!!(user && adminEmails.includes(user.email)));
    });
    return () => unsubscribe();
  }, []);

  // PropertyCard-Komponente
  const PropertyCard = ({ item, isObject = false }) => {
    let statusSpan = null;
    if (!isObject) {
      const status = item.tenantTitle ? 'Vermietet' : 'Freistehend';
      const bgColor = item.tenantTitle ? '#ecfdf5' : '#fef2f2';
      const color = item.tenantTitle ? '#047857' : '#dc2626';
      statusSpan = (
        <span className="status" style={{ backgroundColor: bgColor, color }}>
          {status}
        </span>
      );
    }
    return (
      <div className="property-card">
        <div className="card-header">
          <h3>{isObject ? item.bezeichnung : item.overviewName}</h3>
          {statusSpan}
        </div>
        <p className="address">{item.street} {item.number}</p>
        <p className="city">{item.zipCode} {item.city}</p>
        <div className="property-details">
          <div className="detail-item"><span>Typ</span><strong>{item.type}</strong></div>
          {!isObject && (
            <>
              <div className="detail-item"><span>Größe</span><strong>{formatFloat(item.sizeInSqm)} m²</strong></div>
              <div className="detail-item"><span>Zimmer</span><strong>{item.numberOfRooms}</strong></div>
            </>
          )}
          <div className="detail-item"><span>Monatl. Miete</span><strong>EUR {formatFloat(item.monthlyRent)}</strong></div>
          <div className="detail-item"><span>Miete je m²</span><strong>EUR {formatFloat(item.rentPerSqm)}</strong></div>
          {!isObject && (
            <div className="detail-item"><span>Marktmiete je m²</span><strong>EUR {formatFloat(item.marketAvgRent)}</strong></div>
          )}
          {isObject && (
            <div className="detail-item"><span>Anzahl der Einheiten</span><strong>{item.numberOfUnits}</strong></div>
          )}
        </div>
      </div>
    );
  };

  // Daten laden
  useEffect(() => {
    fetch(`${API_URL}/api/property-overview`)
      .then(r => r.json())
      .then(data => {
        const transformed = data.map(row => ({
          customerId: row.customerid,
          propertyId: row.propertyid,
          overviewName: row.bezeichnungimberblick,
          street: row.street,
          number: row.number,
          zipCode: row.zipcode,
          city: row.city,
          type: row.type,
          sizeInSqm: parseFloat((row.sizeinsqm || '0').replace(',', '.')) || 0,
          numberOfRooms: parseInt(row.numberofrooms || '0', 10),
          monthlyRent: parseFloat((row.monthlyrent || '0').replace(',', '.')) || 0,
          rentPerSqm: parseFloat((row.rentsqm || '0').replace(',', '.')) || 0,
          marketAvgRent: parseFloat((row.marketaveragerent || '0').replace(',', '.')) || 0,
          tenantTitle: row.tenanttitle,
          tenantFirstName: row.tenantprename,
          tenantLastName: row.tenantlastname,
          nextRentIncrease: row.datenextrentincrease,
          maintenanceReserves: parseFloat((row.monthlymaintenancereserves || '0').replace(',', '.')) || 0,
          modernizationReserves: parseFloat((row.monthlymodernizationreserves || '0').replace(',', '.')) || 0,
          serviceType: row.servicetype,
          lastServiceDate: row.dateoflastservice,
          plannedModernizationDate: row.dateofplannedmodernization,
          estimatedModernizationCost: parseFloat((row.estimatedcostofmodernization || '0').replace(',', '.')) || 0,
          rentalStartDate: row.dateofrentalstart,
          rentalEndDate: row.dateofrentalend,
          garageCount: parseInt(row.numberofgarage || '0', 10),
          parkingCount: parseInt(row.numberofparking || '0', 10),
          cellarCount: parseInt(row.numberofcellar || '0', 10),
          playingArea: parseFloat((row.playingareasqm || '0').replace(',', '.')) || 0,
          gardenArea: parseFloat((row.gardenterracebalconysqm || '0').replace(',', '.')) || 0
        }));
        setProperties(transformed);
      })
      .catch(console.error);
  }, [API_URL]);

  useEffect(() => {
    fetch(`${API_URL}/api/object-overview`)
      .then(r => r.json())
      .then(data => {
        const transformed = data.map(row => ({
          customerId: row.customerid,
          objectId: row.objectid,
          bezeichnung: row.bezeichnungimberblick,
          street: row.street,
          number: row.number,
          zipCode: row.zipcode,
          city: row.city,
          type: row.type,
          landArea: parseFloat((row.landareasqm || '0').replace(',', '.')) || 0,
          grossTotalArea: parseFloat((row.grosstotalarea || '0').replace(',', '.')) || 0,
          rentalArea: parseFloat((row.rentalarea || '0').replace(',', '.')) || 0,
          monthlyRent: parseFloat((row.monthlyrent || '0').replace(',', '.')) || 0,
          rentPerSqm: parseFloat((row.rentsqm || '0').replace(',', '.')) || 0,
          numberOfUnits: parseInt(row.numberofunits || '0', 10),
          nextRentIncrease: row.datenextrentincrease,
          maintenanceReserves: parseFloat((row.monthlymaintenancereserves || '0').replace(',', '.')) || 0,
          modernizationReserves: parseFloat((row.monthlymodernizationreserves || '0').replace(',', '.')) || 0,
          serviceType: row.servicetype,
          lastServiceDate: row.dateoflastservice,
          plannedModernizationDate: row.dateofplannedmodernization,
          estimatedModernizationCost: parseFloat((row.estimatedcostofmodernization || '0').replace(',', '.')) || 0,
          garageCount: parseInt(row.numberofgarage || '0', 10),
          parkingCount: parseInt(row.numberofparking || '0', 10),
          cellarCount: parseInt(row.numberofcellar || '0', 10),
          playingArea: parseFloat((row.playingareasqm || '0').replace(',', '.')) || 0,
          gardenArea: parseFloat((row.gardenterracebalconysqm || '0').replace(',', '.')) || 0
        }));
        setObjects(transformed);
      })
      .catch(console.error);
  }, [API_URL]);

  // Filter-Logik
  const filteredProperties = properties
  .filter(p =>
    isAdmin
      ? (selectedCustomerId ? p.customerId === selectedCustomerId : true)
      : p.customerId === selectedCustomerId
  )
  .filter(p => {
    const q = searchQuery.toLowerCase();
    return (
      (p.street?.toLowerCase() || '').includes(q) ||
      (p.city?.toLowerCase() || '').includes(q) ||
      (p.overviewName?.toLowerCase() || '').includes(q)
    );
  });

  const filteredObjects = objects
  .filter(o =>
    isAdmin
      ? (selectedCustomerId ? o.customerId === selectedCustomerId : true)
      : o.customerId === selectedCustomerId
  )
  .filter(o => {
    const q = searchQuery.toLowerCase();
    return (
      (o.street?.toLowerCase() || '').includes(q) ||
      (o.city?.toLowerCase() || '').includes(q) ||
      (o.bezeichnung?.toLowerCase() || '').includes(q)
    );
  });

  const hasObjects = filteredObjects.length > 0;

  useEffect(() => {
    if (!hasObjects && viewMode !== 'property') {
      setViewMode('property');
    }
  }, [hasObjects, viewMode]);

  const closePropertyDetails = () => setSelectedProperty(null);
  const closeObjectDetails = () => setSelectedObject(null);

  return (
    <div className="properties-container">
      <h1>Einheitenübersicht</h1>

      {hasObjects && (
        <div className="view-mode-buttons">
          <button
            onClick={() => setViewMode('property')}
            className={viewMode === 'property' ? 'active' : ''}
          >
            Einheiten-Ansicht
          </button>
          <button
            onClick={() => setViewMode('object')}
            className={viewMode === 'object' ? 'active' : ''}
          >
            Objekt-Ansicht
          </button>
        </div>
      )}

      <input
        type="text"
        placeholder="Nach Straße, Stadt oder Name suchen..."
        className="search-bar"
        value={searchQuery}
        onChange={e => setSearchQuery(e.target.value)}
      />

      {/* EINHEITEN-ANSICHT */}
      {viewMode === 'property' && (
        <>
          <div className="properties-grid">
            {filteredProperties.map(property => (
              <div
                key={property.propertyId}
                onClick={() => setSelectedProperty(property)}
              >
                <PropertyCard item={property} isObject={false} />
              </div>
            ))}
          </div>

          {selectedProperty && (
            <div className="modal-overlay" onClick={closePropertyDetails}>
              <div className="modal-content" onClick={e => e.stopPropagation()}>
                <button className="close-button" onClick={closePropertyDetails}>
                  ×
                </button>

                {/* Modal-Header */}
                <div className="modal-header">
                  <h2>{selectedProperty.overviewName}</h2>
                  <p className="address">
                    {selectedProperty.street} {selectedProperty.number}
                  </p>
                  <p className="city">
                    {selectedProperty.zipCode} {selectedProperty.city}
                  </p>
                </div>

                {/* Modal-Body */}
                <div className="modal-body">
                  <section className="modal-section">
                    <h3>Allgemeine Informationen</h3>
                    <div className="details-grid">
                      <div className="detail-item">
                        <span>Typ</span>
                        <strong>{selectedProperty.type}</strong>
                      </div>
                      <div className="detail-item">
                        <span>Größe</span>
                        <strong>{formatFloat(selectedProperty.sizeInSqm)} m²</strong>
                      </div>
                      <div className="detail-item">
                        <span>Zimmer</span>
                        <strong>{selectedProperty.numberOfRooms}</strong>
                      </div>
                      <div className="detail-item">
                        <span>Monatl. Miete</span>
                        <strong>EUR {formatFloat(selectedProperty.monthlyRent)}</strong>
                      </div>
                      <div className="detail-item">
                        <span>Miete je m²</span>
                        <strong>EUR {formatFloat(selectedProperty.rentPerSqm)}</strong>
                      </div>
                      <div className="detail-item">
                        <span>Marktmiete je m²</span>
                        <strong>EUR {formatFloat(selectedProperty.marketAvgRent)}</strong>
                      </div>
                    </div>
                  </section>
    {/* NEU: Mieter-Informationen */}
    {(selectedProperty.tenantTitle ||
      selectedProperty.tenantFirstName ||
      selectedProperty.tenantLastName) && (
      <section className="modal-section">
        <h3>Mieter</h3>
        <div className="details-grid">
          <div className="detail-item">
            <span>Anrede</span>
            <strong>{selectedProperty.tenantTitle || '-'}</strong>
          </div>
          <div className="detail-item">
            <span>Vorname</span>
            <strong>{selectedProperty.tenantFirstName || '-'}</strong>
         </div>
          <div className="detail-item">
          <span>Nachname</span>
            <strong>{selectedProperty.tenantLastName || '-'}</strong>
         </div>
        </div>
      </section>
    )}

                  <section className="modal-section">
                    <h3>Finanzielle Details</h3>
                    <div className="details-grid">
                      <div className="detail-item">
                        <span>Nächste Mieterhöhung</span>
                        <strong>{formatDate(selectedProperty.nextRentIncrease)}</strong>
                      </div>
                      <div className="detail-item">
                        <span>Instandhaltungsrücklagen (p.M.)</span>
                        <strong>
                          EUR {formatFloat(selectedProperty.maintenanceReserves)}
                        </strong>
                      </div>
                      <div className="detail-item">
                        <span>Modernisierungsrücklagen (p.M.)</span>
                        <strong>
                          EUR {formatFloat(selectedProperty.modernizationReserves)}
                        </strong>
                      </div>
                    </div>
                  </section>

                  <section className="modal-section">
                    <h3>Service-Übersicht</h3>
                    <div className="details-grid">
                      <div className="detail-item">
                        <span>Service-Typ</span>
                        <strong>{selectedProperty.serviceType || '-'}</strong>
                      </div>
                      <div className="detail-item">
                        <span>Datum</span>
                        <strong>{formatDate(selectedProperty.lastServiceDate)}</strong>
                      </div>
                    </div>
                  </section>

                  <section className="modal-section">
                    <h3>Geplante Modernisierungen</h3>
                    <div className="details-grid">
                      <div className="detail-item">
                        <span>Datum</span>
                        <strong>{formatDate(selectedProperty.plannedModernizationDate)}</strong>
                      </div>
                      <div className="detail-item">
                        <span>Geschätzte Kosten</span>
                        <strong>
                          EUR {formatFloat(selectedProperty.estimatedModernizationCost)}
                        </strong>
                      </div>
                    </div>
                  </section>

                  <section className="modal-section">
                    <h3>Vertragsdetails</h3>
                    <div className="details-grid">
                      <div className="detail-item">
                        <span>Mietstart</span>
                        <strong>{formatDate(selectedProperty.rentalStartDate)}</strong>
                      </div>
                      <div className="detail-item">
                        <span>Mietende</span>
                        <strong>
                          {selectedProperty.rentalEndDate
                            ? formatDate(selectedProperty.rentalEndDate)
                            : 'unbefristet'}
                        </strong>
                      </div>
                    </div>
                  </section>

                  <section className="modal-section">
                    <h3>Objektinformation</h3>
                    <div className="details-grid">
                      {selectedProperty.garageCount > 0 && (
                        <div className="detail-item">
                          <span>Anzahl Garagen</span>
                          <strong>{selectedProperty.garageCount}</strong>
                        </div>
                      )}
                      {selectedProperty.parkingCount > 0 && (
                        <div className="detail-item">
                          <span>Anzahl Parkplätze</span>
                          <strong>{selectedProperty.parkingCount}</strong>
                        </div>
                      )}
                      {selectedProperty.cellarCount > 0 && (
                        <div className="detail-item">
                          <span>Anzahl Keller</span>
                          <strong>{selectedProperty.cellarCount}</strong>
                        </div>
                      )}
                      {selectedProperty.playingArea > 0 && (
                        <div className="detail-item">
                          <span>Spielfläche</span>
                          <strong>{formatFloat(selectedProperty.playingArea)} m²</strong>
                        </div>
                      )}
                      {selectedProperty.gardenArea > 0 && (
                        <div className="detail-item">
                          <span>Garten/Terrasse/Balkon</span>
                          <strong>{formatFloat(selectedProperty.gardenArea)} m²</strong>
                        </div>
                      )}
                    </div>
                  </section>
                </div>

                <div className="modal-footer">
                  <button
                    className="contract-button-small"
                    onClick={() => navigate('/documents?docType=Mietvertrag')}
                  >
                    Zu den Dokumenten
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* OBJEKT-ANSICHT */}
      {viewMode === 'object' && (
        <>
          <div className="properties-grid">
            {filteredObjects.map(object => (
              <div key={object.objectId} onClick={() => setSelectedObject(object)}>
                <PropertyCard item={object} isObject />
              </div>
            ))}
          </div>

          {selectedObject && (
            <div className="modal-overlay" onClick={closeObjectDetails}>
              <div className="modal-content" onClick={e => e.stopPropagation()}>
                <button className="close-button" onClick={closeObjectDetails}>
                  ×
                </button>

                <div className="modal-header">
                  <h2>{selectedObject.bezeichnung || 'Objektdetails'}</h2>
                  <p className="address">
                    {selectedObject.street} {selectedObject.number}
                  </p>
                  <p className="city">
                    {selectedObject.zipCode} {selectedObject.city}
                  </p>
                </div>

                <div className="modal-body">
                  <section className="modal-section">
                    <h3>Finanzielle Details</h3>
                    <div className="details-grid">
                      <div className="detail-item">
                        <span>Nächste Mieterhöhung</span>
                        <strong>{formatDate(selectedObject.nextRentIncrease)}</strong>
                      </div>
                      <div className="detail-item">
                        <span>Instandhaltungsrücklagen (p.M.)</span>
                        <strong>
                          EUR {formatFloat(selectedObject.maintenanceReserves)}
                        </strong>
                      </div>
                      <div className="detail-item">
                        <span>Modernisierungsrücklagen (p.M.)</span>
                        <strong>
                          EUR {formatFloat(selectedObject.modernizationReserves)}
                        </strong>
                      </div>
                    </div>
                  </section>

                  <section className="modal-section">
                    <h3>Service-Übersicht</h3>
                    <div className="details-grid">
                      <div className="detail-item">
                        <span>Service-Typ</span>
                        <strong>{selectedObject.serviceType || '-'}</strong>
                      </div>
                      <div className="detail-item">
                        <span>Datum</span>
                        <strong>{formatDate(selectedObject.lastServiceDate)}</strong>
                      </div>
                    </div>
                  </section>

                  <section className="modal-section">
                    <h3>Geplante Modernisierungen</h3>
                    <div className="details-grid">
                      <div className="detail-item">
                        <span>Datum</span>
                        <strong>{formatDate(selectedObject.plannedModernizationDate)}</strong>
                      </div>
                      <div className="detail-item">
                        <span>Geschätzte Kosten</span>
                        <strong>
                          EUR {formatFloat(selectedObject.estimatedModernizationCost)}
                        </strong>
                      </div>
                    </div>
                  </section>

                  <section className="modal-section">
                    <h3>Objektinformation</h3>
                    <div className="details-grid">
                      <div className="detail-item">
                        <span>Grundstücksfläche</span>
                        <strong>{formatFloat(selectedObject.landArea)} m²</strong>
                      </div>
                      <div className="detail-item">
                        <span>Bruttogrundfläche (BGF)</span>
                        <strong>{formatFloat(selectedObject.grossTotalArea)} m²</strong>
                      </div>
                      <div className="detail-item">
                        <span>Mietfläche</span>
                        <strong>{formatFloat(selectedObject.rentalArea)} m²</strong>
                      </div>
                      {selectedObject.garageCount > 0 && (
                        <div className="detail-item">
                          <span>Anzahl der Garagen</span>
                          <strong>{selectedObject.garageCount}</strong>
                        </div>
                      )}
                      {selectedObject.parkingCount > 0 && (
                        <div className="detail-item">
                          <span>Anzahl der Parkplätze</span>
                          <strong>{selectedObject.parkingCount}</strong>
                        </div>
                      )}
                      {selectedObject.cellarCount > 0 && (
                        <div className="detail-item">
                          <span>Anzahl der Keller</span>
                          <strong>{selectedObject.cellarCount}</strong>
                        </div>
                      )}
                      {selectedObject.playingArea > 0 && (
                        <div className="detail-item">
                          <span>Spielfläche</span>
                          <strong>{formatFloat(selectedObject.playingArea)} m²</strong>
                        </div>
                      )}
                      {selectedObject.gardenArea > 0 && (
                        <div className="detail-item">
                          <span>Garten/Terrasse/Balkon</span>
                          <strong>{formatFloat(selectedObject.gardenArea)} m²</strong>
                        </div>
                      )}
                    </div>
                  </section>
                </div>

                <div className="modal-footer">
                  <button
                    className="contract-button-small"
                    onClick={() => navigate('/documents?docType=Mietvertrag')}
                  >
                    Zu den Dokumenten
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default Properties;
